<template>
  <div>
    <h3 class="w-full mt-2 mb-6 px-4 py-2 bg-print-order-teal-lightest font-semibold text-sm">Property #{{ propertyNumber + 1 }} Details</h3>
    <loader v-if="!propertyFields" />
    <div v-else class="flex flex-wrap lg:flex-row">
      <div class="w-full lg:w-1/2 px-4 lg:pr-3">
        <label for="display_address" class="block text-sm mb-2">House Name/Number:</label>
        <input type="text" id="display_address" v-model="property.display_address" class="w-full border mb-4 px-2 py-3 leading-tight">
        <label for="location" class="block text-sm mb-2">Town/County:</label>
        <input type="text" id="location" v-model="property.location" class="w-full border mb-4 px-2 py-3 leading-tight">
        <div class="flex flex-wrap">
          <div class="w-full mb-4 md:w-1/2 md:pr-2">
            <label for="price" class="block text-sm mb-2">Price:</label>
            <input type="text" id="price" v-model="property.price" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div class="w-full mb-4 md:w-1/2 md:pl-2">
            <label for="price_qualifier" class="block text-sm mb-2" v-if="orderItem.department === 'Lettings'">Rent Frequency:</label>
            <label for="price_qualifier" class="block text-sm mb-2" v-else>Price Qualifier:</label>
            <select id="price_qualifier" v-model="property.price_qualifier_id" class="w-full border px-2 py-3" v-if="orderItem.department === 'Sales'">
              <option :value="null">Select</option>
              <option v-for="(option, i) in propertyEnums.price_qualifier" :key="i" :value="option.value">{{ option.label }}</option>
            </select>
            <select id="price_qualifier" v-model="property.price_qualifier_id" class="w-full border px-2 py-3" v-if="orderItem.department === 'Lettings'">
              <option :value="null">Select</option>
              <option v-for="(option, i) in propertyEnums.rent_frequency" :key="i" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <div class="w-full mb-4 md:w-1/2 md:pr-2">
            <label for="bedrooms" class="block text-sm mb-2">Bedrooms:</label>
            <input type="number" id="bedrooms" v-model="property.bedrooms" min="0" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div class="w-full mb-4 md:w-1/2 md:pl-2">
            <label for="bathrooms" class="block text-sm mb-2">Bathrooms:</label>
            <input type="number" id="bathrooms" v-model="property.bathrooms" min="0" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div class="w-full mb-4 md:w-1/2 md:pr-2">
            <label for="reception_rooms" class="block text-sm mb-2">Reception Rooms:</label>
            <input type="number" id="reception_rooms" v-model="property.reception_rooms" min="0" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div v-if="propertyFields.includes('garages')" class="w-full mb-4 md:w-1/2 md:pl-2">
            <label for="garages" class="block text-sm mb-2">Garages:</label>
            <input type="number" id="garages" v-model="property.garages" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div v-if="propertyFields.includes('pool')" class="w-full mb-4 md:w-1/2 md:pr-2">
            <label for="pool" class="block text-sm mb-2">Swimming Pool:</label>
            <select v-model="property.pool" class="w-full border px-2 py-3">
              <option :value="null">Select</option>
              <option :value="true">Yes</option>
              <option :value="false">No</option>
            </select>
          </div>
          <div v-if="propertyFields.includes('tenure_id')" class="w-full mb-4 md:w-1/2 md:pl-2">
            <label for="price" class="block text-sm mb-2">Tenure:</label>
            <select v-model="property.tenure_id" class="w-full border px-2 py-3">
              <option :value="null">Select</option>
              <option v-for="(option, i) in propertyEnums.tenure" :key="i" :value="option.value">{{ option.label }}</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          <div v-if="propertyFields.includes('epc')" class="w-full mb-4 md:w-1/2 md:pr-2">
            <label for="price" class="block text-sm mb-2">EPC Rating:</label>
            <select v-model="property.epc" class="w-full border px-2 py-3">
              <option :value="null">Select</option>
              <option value="A">EPC Rating A</option>
              <option value="B">EPC Rating B</option>
              <option value="C">EPC Rating C</option>
              <option value="D">EPC Rating D</option>
              <option value="E">EPC Rating E</option>
              <option value="F">EPC Rating F</option>
              <option value="G">EPC Rating G</option>
              <option value="Pending">Pending</option>
              <option value="Exempt">Exempt</option>
            </select>
          </div>
          <div v-if="propertyFields.includes('council_tax_band')" class="w-full mb-4 md:w-1/2 md:pl-2">
            <label for="price" class="block text-sm mb-2">Council Tax Band:</label>
            <select v-model="property.council_tax_band" class="w-full border px-2 py-3">
              <option :value="null">Select</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
              <option value="E">E</option>
              <option value="F">F</option>
              <option value="G">G</option>
              <option value="H">H</option>
              <option value="I">I</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          <div v-if="propertyFields.includes('land_size')" class="w-full">
            <!-- Break to new line -->
          </div>
          <div v-if="propertyFields.includes('land_size')" class="w-full mb-4 md:w-1/2 md:pr-2">
            <label for="land_size" class="block text-sm mb-2">Land Size:</label>
            <input type="number" id="land_size" v-model="property.land_size" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div v-if="propertyFields.includes('land_size_unit')" class="w-full mb-4 md:w-1/2 md:pl-2">
            <label for="land_size_unit" class="block text-sm mb-2">Land Size Unit:</label>
            <select v-model="property.land_size_unit" class="w-full border px-2 py-3">
              <option :value="null">Select</option>
              <option value="2">Square Metres</option>
              <option value="1">Square Feet</option>
            </select>
          </div>
          <div v-if="propertyFields.includes('floor_size')" class="w-full mb-4 md:w-1/2 md:pr-2">
            <label for="floor_size" class="block text-sm mb-2">Floor Size:</label>
            <input type="number" id="floor_size" v-model="property.floor_size" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div v-if="propertyFields.includes('floor_size_unit')" class="w-full mb-4 md:w-1/2 md:pl-2">
            <label for="floor_size_unit" class="block text-sm mb-2">Floor Size Unit:</label>
            <select v-model="property.floor_size_unit" class="w-full border px-2 py-3">
              <option :value="null">Select</option>
              <option value="2">Square Metres</option>
              <option value="1">Square Feet</option>
            </select>
          </div>
          <div class="w-full mb-4 md:w-1/2 md:pr-2" v-if="orderItem.department === 'Lettings'">
            <label for="price" class="block text-sm mb-2">Holding Deposit:</label>
            <input type="text" id="holding_deposit" v-model="property.holding_deposit" class="w-full border px-2 py-3 leading-tight">
          </div>
          <div class="w-full mb-4 md:w-1/2 md:pl-2" v-if="orderItem.department === 'Lettings'">
            <label for="price" class="block text-sm mb-2">Security Deposit:</label>
            <input type="text" id="security_deposit" v-model="property.security_deposit" class="w-full border px-2 py-3 leading-tight">
          </div>
        </div>
      </div>
      <div class="w-full lg:w-1/2 px-4 lg:pl-3">
        <div class="flex flex-wrap items-baseline">
          <label for="summary" class="w-2/3 block text-sm mb-2">Summary:</label>
          <span v-if="wordCount > summaryWordLimit" class="w-1/3 text-right text-xs text-red">Over limit</span>
          <span v-else class="w-1/3 text-right text-xs">{{ wordCount + '/' + summaryWordLimit }}</span>
        </div>
        <textarea
          v-model="property.summary"
          rows="6"
          id="summary"
          class="w-full mb-6 input bg-white"
          :placeholder="summaryPlaceholder"
        ></textarea>

        <div v-if="getImageCount(propertyNumber) > 0" class="w-full">
          <label class="block text-sm mb-2">File Upload <span class="text-xs font-normal">({{ getImageCount(propertyNumber) }} {{ getImageCount(propertyNumber) === 1 ? 'image' : 'images' }} required)</span></label>
          <div id="cpo-dropzone" class="border border-dashed rounded-lg py-6 px-4">
            <div v-if="property.attachments && property.attachments.length > 0 || property.uploads && property.uploads.length > 0" class="w-full border-bottom border-dashed mb-6 pb-4">
              <table>
                <tbody>
                  <tr v-for="(file, j) in property.attachments.filter(file => !file.is_qr_code)" :key="j" class="h-12">
                    <td class="inline-block w-12 h-12 mr-2 my-1">
                      <img :src="file.thumb" :alt="file.filename" loading="lazy" class="block w-12 min-h-8 h-full bg-grey-lighter object-contain" />
                    </td>
                    <td class="w-full">{{ file.filename }}</td>
                    <td>
                      <i class="fas fa-check-circle text-print-order-teal fa-2x pl-2"></i>
                    </td>
                    <td>
                      <i class="fas fa-trash-alt text-grey fa-2x pl-2 cursor-pointer" @click="deleteAsset(property, file)"></i>
                    </td>
                  </tr>
                  <tr v-for="(file, j) in property.uploads" :key="100 + j" class="h-12">
                    <td class="inline-block w-12 h-12 inline-block mr-2 my-1">
                      <div class="block w-12 min-h-8 h-full bg-grey-lighter object-contain"></div>
                    </td>
                    <td class="w-full">{{ file.filename }}</td>
                    <td colspan="2">
                      <i v-if="file.complete" class="fas fa-check-circle text-print-order-teal fa-2x pl-2"></i>
                      <gauge-graph v-else :series="[{ color: '#eee', value: 101 },{ color: '#0d9eff', value: file.progress }]" :maximum="101" :arc-size="360" :offset="0" class="w-8 h-8">
                      </gauge-graph>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <upload-dropzone v-if="property.display_address" @change="uploadFiles($event, property)" accept="png" containerClass="pb-2" />
            <p v-else class="text-grey">Please enter the House Name/Number for the property before uploading images.</p>
          </div>
        </div>
        <div class="w-full mt-4">
          <label class="block text-sm mb-2">QR Code Upload <span class="text-xs font-normal">(optional)</span></label>
          <div id="cpo-dropzone" class="border border-dashed rounded-lg py-6 px-4">
            <div v-if="orderItem.attachments && orderItem.attachments.length > 0 || orderItem.uploads && orderItem.uploads.length > 0" class="w-full border-bottom border-dashed mb-6 pb-4">
              <table>
                <tbody>
                  <tr v-for="(file, i) in orderItem.attachments.filter(file => file.is_qr_code)" :key="i" class="h-12">
                    <td class="inline-block w-12 h-12 mr-2 my-1">
                      <img :src="file.thumb" :alt="file.filename" loading="lazy" class="block w-12 min-h-8 h-full bg-grey-lighter object-contain" />
                    </td>
                    <td class="w-full">{{ file.filename }}</td>
                    <td>
                      <i class="fas fa-check-circle text-print-order-teal fa-2x pl-2"></i>
                    </td>
                    <td>
                      <i class="fas fa-trash-alt text-grey fa-2x pl-2 cursor-pointer" @click="deleteAsset(property, file)"></i>
                    </td>
                  </tr>
                  <tr v-for="(file, i) in orderItem.uploads.filter(file => file.is_qr_code)" :key="100 + i" class="h-12">
                    <td class="inline-block w-12 h-12 inline-block mr-2 my-1">
                      <div class="block w-12 min-h-8 h-full bg-grey-lighter object-contain"></div>
                    </td>
                    <td class="w-full">{{ file.filename }}</td>
                    <td colspan="2">
                      <i v-if="file.complete" class="fas fa-check-circle text-print-order-teal fa-2x pl-2"></i>
                      <gauge-graph v-else :series="[{ color: '#eee', value: 101 },{ color: '#0d9eff', value: file.progress }]" :maximum="101" :arc-size="360" :offset="0" class="w-8 h-8">
                      </gauge-graph>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <upload-dropzone @change="uploadQrFiles($event)" accept="png" containerClass="pb-2" />
          </div>
        </div>
      </div>
      </div>
    </div>
</template>
<script>
import api, { v2Api } from '@/http'
import http from 'axios'
import { mapActions } from 'vuex'
import GaugeGraph from '@/components/Graphs/GaugeGraph'
import UploadDropzone from '@/components/UploadDropzone'

export default {
  data () {
    return {
      wordCount: 0
    }
  },
  components: {
    GaugeGraph,
    UploadDropzone
  },
  props: [
    'property',
    'propertyNumber',
    'propertySpec',
    'propertyFields',
    'propertyEnums',
    'orderItem',
    'officeId',
    'errorBag'
  ],
  computed: {
    count () {
      if (this.propertySpec && this.propertySpec.count) {
        return this.propertySpec.count
      }

      return 0
    },
    summaryWordLimit () {
      if (this.propertySpec && this.propertySpec.words_per_property) {
        return this.propertySpec.words_per_property
      }

      return 0
    },
    summaryPlaceholder () {
      if (this.summaryWordLimit) {
        return 'Please type the property summary here using a maximum of ' + this.summaryWordLimit + ' words.'
      }

      return 'Please type the property summary here'
    },
    summary () {
      return this.property.summary
    }
  },
  created () {
  },
  watch: {
    summary: {
      handler (summary) {
        var words = summary.split(/[\s]+/)
        this.wordCount = words.length
      }
    }
  },
  methods: {
    ...mapActions([
      'displayToastNotification'
    ]),
    uploadFiles (files, property) {
      for (let i = 0; i < files.length; i++) {
        this.uploadFile(files[i], property)
      }
    },
    async uploadFile (file, property) {
      if ((file.size / 1000) < 500) {
        return this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: 'File uploads need a file size of at least 500kB to ensure image quality'
        })
      }
      let attachment = {
        filename: file.name,
        size: file.size,
        progress: 0,
        complete: false
      }
      property.uploads.push(attachment)

      let filePath = `custom-print-orders/${this.officeId}`

      const extension = file.name.split('.').pop()
      const response = await api.post('signed-upload', {
        'disk': 's3',
        'extension': extension,
        'path': filePath,
        'visibility': 'public-read',
        'content_type': file.type
      })

      let s3File = response.data.key
      let headers = response.data.headers
      if ('Host' in headers) {
        delete headers.Host
      }

      await http.put(response.data.url, file, {
        headers: headers,
        onUploadProgress: (progressEvent) => {
          attachment.progress = Math.ceil(progressEvent.loaded / progressEvent.total * 100)
        }
      })

      let data = {
        office_id: this.officeId,
        file_path: s3File,
        file_name: property.display_address.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '_' + file.name
      }

      if (property.id) {
        data.property_id = property.id
      }

      if (this.orderItem.id) {
        data.item_id = this.orderItem.id
      }

      v2Api.post('custom-print-order-attachments', data).then((response) => {
        attachment.complete = true
        property.uploads.splice(property.uploads.indexOf(attachment), 1)
        property.attachments.push(response.data.attachment)
        this.orderItem.attachments.push(response.data.attachment)
      }, (errors) => {
        property.uploads.splice(property.uploads.indexOf(attachment), 1)
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: errors.response.data.errors
        })
      })
    },
    uploadQrFiles (files) {
      for (let i = 0; i < files.length; i++) {
        this.uploadQrFile(files[i])
      }
    },
    async uploadQrFile (file) {
      let attachment = {
        filename: file.name,
        size: file.size,
        progress: 0,
        complete: false,
        is_qr_code: true
      }
      this.orderItem.uploads.push(attachment)

      let filePath = `custom-print-orders/${this.officeId}`

      const extension = file.name.split('.').pop()
      const response = await api.post('signed-upload', {
        'disk': 's3',
        'extension': extension,
        'path': filePath,
        'visibility': 'public-read',
        'content_type': file.type
      })

      let s3File = response.data.key
      let headers = response.data.headers
      if ('Host' in headers) {
        delete headers.Host
      }

      await http.put(response.data.url, file, {
        headers: headers,
        onUploadProgress: (progressEvent) => {
          attachment.progress = Math.ceil(progressEvent.loaded / progressEvent.total * 100)
        }
      })

      let data = {
        office_id: this.officeId,
        file_path: s3File,
        file_name: file.name,
        is_qr_code: true
      }

      if (this.orderItem.id) {
        data.item_id = this.orderItem.id
      }

      v2Api.post('custom-print-order-attachments', data).then((response) => {
        attachment.complete = true
        this.orderItem.attachments.push(response.data.attachment)
        this.orderItem.uploads.splice(this.orderItem.uploads.indexOf(attachment), 1)
      }, (errors) => {
        this.orderItem.uploads.splice(this.orderItem.uploads.indexOf(attachment), 1)
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: errors.response.data.errors
        })
      })
    },
    deleteAsset (property, file) {
      if (confirm('Are you sure you would like to delete the file upload?')) {
        v2Api.delete(`custom-print-order-attachments/${file.id}`).then((response) => {
          property.attachments = property.attachments.filter(attachment => attachment.id !== file.id)
          this.orderItem.attachments = this.orderItem.attachments.filter(attachment => attachment.id !== file.id)
        })
      }
    },
    getImageCount (propertyNumber = 0) {
      if (this.propertySpec && this.propertySpec.image_counts) {
        return this.propertySpec.image_counts[propertyNumber]
      }

      if (this.propertySpec && this.propertySpec.images_per_property) {
        return this.propertySpec.images_per_property
      }

      return 0
    }
  }
}
</script>
<style lang="scss" scoped>
  #cpo-dropzone table td {
    word-break: break-all;
  }
</style>
